import { FC, memo, useState } from 'react';
import { Box, Container, Alert, Typography, Divider as MuiDivider } from '@mui/material';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { UserProvider } from '../UsersPage/context/userContext';
import { RequestProvider } from '../RequestsPage/context/requestContext';
import { OfferProvider } from '../RequestsPage/context/offerContext';
import { CreateRequestProvider } from './context/createRequestContext';
import { useCreateRequestContext } from './hook/useCreateRequestContext';
import {
    FormTextField,
    FormPhoneInput,
    FormSection,
    FormActions,
} from '../../common/Form';
import {
    requestCreateSchema,
    defaultRequestFormValues,
    type RequestCreateFormData,
} from './schemas';
import OfferBuilderMUI from './component/OfferBuilderMUI';
import RequestCreationConfirmModal from './component/RequestCreationConfirmModal';
import { RequestCreationType } from './type/enums/RequestCreationType';
import { MessageContentType } from './type/enums/MessageContentType';


interface IRequestCreatePage {}

const RequestCreatePage: FC = () => {
    const { createRequest, createdRequest } = useCreateRequestContext();
    const navigate = useNavigate();
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [createRequestModalOpened, setCreateRequestModalOpened] = useState(false);
    const methods = useForm<RequestCreateFormData>({
        resolver: zodResolver(requestCreateSchema),
        defaultValues: defaultRequestFormValues,
        mode: 'onBlur',
    });

    const {
        handleSubmit,
        reset,
        control,
        formState: { isSubmitting },
    } = methods;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'offerPositions',
    });

    const onSubmit = async (data: RequestCreateFormData) => {
        try {
            const clientPhones = data.clientPhones
                ? data.clientPhones
                    .split(',')
                    .map((phone: string) => phone.trim()).filter(Boolean)
                : [];
            const messages: any[] = [];

            if (data.carDocument && data.carDocument.length > 0) {
                messages.push({
                    messageType: MessageContentType.IMAGE,
                    img: data.carDocument[0],
                });
            }

            messages.push({
                messageType: MessageContentType.TEXT,
                text: data.description && data.description.length > 0
                    ? data.description
                    : 'Welcome to iAuto. This is your offer',
            });

            messages.push({
                messageType: MessageContentType.OFFER,
                offer: { offerPositions: data.offerPositions },
            });

            const newRequest: any = {
                clientPhones: clientPhones,
                messages: messages,
                operatorPhone: [data.operatorPhone],
                creationType: RequestCreationType.CREATED_BY_ADMIN,
                sendTelegram: true,
            };

            await createRequest(newRequest);
            setSubmitSuccess(true);
            setCreateRequestModalOpened(true);

            setTimeout(() => {
                reset();
                setSubmitSuccess(false);
            }, 2000);
        } catch (error) {
            console.error('Failed to create request:', error);
        }
    };

    const handleCancel = () => {
        navigate('/requests');
    };

    const closeCreateRequestModal = () => {
        setCreateRequestModalOpened(false);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <FormProvider {...methods}>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    {submitSuccess && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Request created successfully!
                        </Alert>
                    )}

                    <FormSection
                        title="New Request"
                        description="Create a new request with operator and optional client phones"
                        columns={2}
                        elevation={2}
                    >
                        <FormTextField
                            name="clientPhones"
                            label="Client Phones (Optional)"
                            placeholder="+37369123456, +37369234567"
                            helperText="Enter multiple phone numbers separated by commas"
                        />

                        <FormPhoneInput
                            name="operatorPhone"
                            label="Operator Phone"
                            required
                            defaultCountry="MD"
                            showCountrySelect
                            helperText="Required - operator's phone number"
                        />

                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <FormTextField
                                name="description"
                                label="Description"
                                placeholder="Enter description"
                                multiline
                                rows={4}
                                helperText="Optional - will use default message if empty"
                            />
                        </Box>
                    </FormSection>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                            Offer Positions
                        </Typography>
                        <MuiDivider sx={{ mb: 3 }} />

                        <OfferBuilderMUI
                            fields={fields}
                            append={append}
                            remove={remove}
                        />
                    </Box>
                    <FormActions
                        onCancel={handleCancel}
                        submitLabel="Submit Request"
                        cancelLabel="Cancel"
                        isSubmitting={isSubmitting}
                    />
                </Box>
            </FormProvider>
            <RequestCreationConfirmModal
                open={createRequestModalOpened}
                onClose={closeCreateRequestModal}
                requests={[createdRequest]}
            />
        </Container>
    );
};

const RequestCreateProviderHOC: FC<IRequestCreatePage> = memo((props: IRequestCreatePage) => {
    return (
        <UserProvider>
            <RequestProvider>
                <CreateRequestProvider>
                    <OfferProvider>
                        <RequestCreatePage {...props} />
                    </OfferProvider>
                </CreateRequestProvider>
            </RequestProvider>
        </UserProvider>
    );
});

export default RequestCreateProviderHOC;
